'use client';

import { c as _c } from "react/compiler-runtime";
import { type MouseEvent, PropsWithChildren, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence, type Transition } from 'motion/react';
import { FocusScope } from '@radix-ui/react-focus-scope';
import { usePrefersReducedMotion } from './usePrefersReducedMotion';
import { useKeyboardInset } from './useKeyboardInset';
import { MORPH_TRANSITION } from './constants';
type MorphDialogContentProps = {
  morphId: string;
  isOpen: boolean;
  ariaLabel?: string;
  className?: string;
  maxWidth?: string;
  fullscreen?: boolean;
  /** Overrides the layout/morph transition (default: MORPH_TRANSITION.layout). */
  transition?: Transition;
  onClose: () => void;
};
export function MorphDialogContent(t0) {
  const $ = _c(52);
  const {
    morphId,
    isOpen,
    onClose,
    ariaLabel,
    className,
    maxWidth: t1,
    fullscreen: t2,
    transition: t3,
    children
  } = t0;
  const maxWidth = t1 === undefined ? "800px" : t1;
  const fullscreen = t2 === undefined ? false : t2;
  const transition = t3 === undefined ? MORPH_TRANSITION.layout : t3;
  const prefersReducedMotion = usePrefersReducedMotion();
  const panelRef = useRef(null);
  const keyboardInset = useKeyboardInset(isOpen && fullscreen, panelRef);
  let t4;
  if ($[0] !== onClose) {
    t4 = event => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    $[0] = onClose;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const handleEscape = t4;
  let t5;
  let t6;
  if ($[2] !== handleEscape || $[3] !== isOpen) {
    t5 = () => {
      if (!isOpen) {
        return;
      }
      document.addEventListener("keydown", handleEscape);
      return () => document.removeEventListener("keydown", handleEscape);
    };
    t6 = [isOpen, handleEscape];
    $[2] = handleEscape;
    $[3] = isOpen;
    $[4] = t5;
    $[5] = t6;
  } else {
    t5 = $[4];
    t6 = $[5];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[6] !== isOpen) {
    t7 = () => {
      if (!isOpen) {
        return;
      }
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    };
    t8 = [isOpen];
    $[6] = isOpen;
    $[7] = t7;
    $[8] = t8;
  } else {
    t7 = $[7];
    t8 = $[8];
  }
  useEffect(t7, t8);
  let t9;
  if ($[9] !== onClose) {
    t9 = function handleBackdropClick(event_0) {
      if (event_0.target === event_0.currentTarget) {
        onClose();
      }
    };
    $[9] = onClose;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  const handleBackdropClick = t9;
  if (prefersReducedMotion) {
    if (!isOpen) {
      return null;
    }
    let t10;
    if ($[11] !== fullscreen || $[12] !== handleBackdropClick) {
      t10 = !fullscreen ? <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" onClick={handleBackdropClick} /> : null;
      $[11] = fullscreen;
      $[12] = handleBackdropClick;
      $[13] = t10;
    } else {
      t10 = $[13];
    }
    const t11 = `outline-none ring-0 focus:ring-0 focus-visible:ring-0 [&>*]:outline-none [&>*]:ring-0 [&>*]:focus:ring-0 [&>*]:focus-visible:ring-0 ${fullscreen ? `fixed inset-0 z-[60] overflow-y-auto bg-white ${className ?? ""}` : `fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[60] w-[calc(100%-48px)] overflow-y-auto rounded-3xl bg-white p-6 ${className ?? ""}`}`;
    let t12;
    if ($[14] !== fullscreen || $[15] !== maxWidth) {
      t12 = fullscreen ? undefined : {
        maxWidth,
        maxHeight: "calc(100% - 32px)",
        minHeight: 348
      };
      $[14] = fullscreen;
      $[15] = maxWidth;
      $[16] = t12;
    } else {
      t12 = $[16];
    }
    const t13 = `flex flex-col gap-6 ${fullscreen ? "h-full" : ""}`;
    let t14;
    if ($[17] !== fullscreen || $[18] !== keyboardInset) {
      t14 = fullscreen ? {
        paddingBottom: keyboardInset
      } : undefined;
      $[17] = fullscreen;
      $[18] = keyboardInset;
      $[19] = t14;
    } else {
      t14 = $[19];
    }
    let t15;
    if ($[20] !== children || $[21] !== t13 || $[22] !== t14) {
      t15 = <div className={t13} style={t14}>{children}</div>;
      $[20] = children;
      $[21] = t13;
      $[22] = t14;
      $[23] = t15;
    } else {
      t15 = $[23];
    }
    let t16;
    if ($[24] !== ariaLabel || $[25] !== t11 || $[26] !== t12 || $[27] !== t15) {
      t16 = <FocusScope trapped={true} loop={true}><div ref={panelRef} role="dialog" aria-modal="true" aria-label={ariaLabel} className={t11} style={t12}>{t15}</div></FocusScope>;
      $[24] = ariaLabel;
      $[25] = t11;
      $[26] = t12;
      $[27] = t15;
      $[28] = t16;
    } else {
      t16 = $[28];
    }
    let t17;
    if ($[29] !== t10 || $[30] !== t16) {
      t17 = <div className="fixed inset-0 z-[60]">{t10}{t16}</div>;
      $[29] = t10;
      $[30] = t16;
      $[31] = t17;
    } else {
      t17 = $[31];
    }
    return t17;
  }
  let t10;
  if ($[32] !== fullscreen || $[33] !== handleBackdropClick || $[34] !== isOpen || $[35] !== morphId) {
    t10 = isOpen && !fullscreen && <motion.div key={`morph-backdrop-${morphId}`} className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={MORPH_TRANSITION.backdrop} onClick={handleBackdropClick} />;
    $[32] = fullscreen;
    $[33] = handleBackdropClick;
    $[34] = isOpen;
    $[35] = morphId;
    $[36] = t10;
  } else {
    t10 = $[36];
  }
  let t11;
  if ($[37] !== t10) {
    t11 = <AnimatePresence>{t10}</AnimatePresence>;
    $[37] = t10;
    $[38] = t11;
  } else {
    t11 = $[38];
  }
  let t12;
  if ($[39] !== ariaLabel || $[40] !== children || $[41] !== className || $[42] !== fullscreen || $[43] !== isOpen || $[44] !== keyboardInset || $[45] !== maxWidth || $[46] !== morphId || $[47] !== transition) {
    t12 = isOpen && <div className={`fixed inset-0 z-[60] flex pointer-events-none ${fullscreen ? "" : "items-center justify-center"}`}><motion.div ref={panelRef} layoutId={`morph-container-${morphId}`} role="dialog" aria-modal="true" aria-label={ariaLabel} className={`outline-none ring-0 focus:ring-0 focus-visible:ring-0 [&>*]:outline-none [&>*]:ring-0 [&>*]:focus:ring-0 [&>*]:focus-visible:ring-0 ${fullscreen ? `pointer-events-auto w-full h-full overflow-y-auto bg-white ${className ?? ""}` : `pointer-events-auto w-[calc(100%-48px)] overflow-y-auto bg-white p-6 ${className ?? ""}`}`} style={fullscreen ? {
        borderRadius: 0,
        originY: 1
      } : {
        maxWidth,
        maxHeight: "calc(100% - 32px)",
        minHeight: 348,
        borderRadius: 24,
        originY: 1
      }} transition={transition}><FocusScope trapped={true} loop={true} className={fullscreen ? "h-full w-full" : undefined}><div className={`flex flex-col gap-6 ${fullscreen ? "h-full" : ""}`} style={fullscreen ? {
            paddingBottom: keyboardInset
          } : undefined}>{children}</div></FocusScope></motion.div></div>;
    $[39] = ariaLabel;
    $[40] = children;
    $[41] = className;
    $[42] = fullscreen;
    $[43] = isOpen;
    $[44] = keyboardInset;
    $[45] = maxWidth;
    $[46] = morphId;
    $[47] = transition;
    $[48] = t12;
  } else {
    t12 = $[48];
  }
  let t13;
  if ($[49] !== t11 || $[50] !== t12) {
    t13 = <>{t11}{t12}</>;
    $[49] = t11;
    $[50] = t12;
    $[51] = t13;
  } else {
    t13 = $[51];
  }
  return t13;
}